import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1/public`;
const CACHE_KEY = "erimus_public_cache";
const FAV_KEY = "erimus_saude_favoritos";
const HISTORY_KEY = "erimus_saude_historico";
const CREDS_KEY = "erimus_saude_chaves";

export const publicApi = axios.create({ baseURL: BASE, timeout: 20000 });

const readStore = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) ?? fallback;
  } catch {
    return fallback;
  }
};
const writeStore = (key, value) => localStorage.setItem(key, JSON.stringify(value));

/** Cache local dos conteúdos públicos já acessados (leitura com conexão instável). */
export const cachedGet = async (path, params) => {
  const cacheId = `${path}?${new URLSearchParams(params || {}).toString()}`;
  const cache = readStore(CACHE_KEY, {});
  try {
    const { data } = await publicApi.get(path, { params });
    writeStore(CACHE_KEY, { ...cache, [cacheId]: { data, at: Date.now() } });
    return { data, fromCache: false };
  } catch (error) {
    if (cache[cacheId]) return { data: cache[cacheId].data, fromCache: true };
    throw error;
  }
};

export const favorites = {
  list: () => readStore(FAV_KEY, []),
  has: (slug) => readStore(FAV_KEY, []).some((f) => f.slug === slug),
  toggle: (item) => {
    const current = readStore(FAV_KEY, []);
    const exists = current.some((f) => f.slug === item.slug);
    const next = exists ? current.filter((f) => f.slug !== item.slug) : [{ ...item, saved_at: Date.now() }, ...current];
    writeStore(FAV_KEY, next);
    if (!exists) trackEvent("content_favorited", { slug: item.slug, content_type: item.type });
    return !exists;
  },
};

export const history = {
  list: () => readStore(HISTORY_KEY, []),
  push: (item) => {
    const current = readStore(HISTORY_KEY, []).filter((h) => h.slug !== item.slug);
    writeStore(HISTORY_KEY, [{ ...item, seen_at: Date.now() }, ...current].slice(0, 30));
  },
  last: () => readStore(HISTORY_KEY, [])[0] || null,
  clear: () => writeStore(HISTORY_KEY, []),
};

/** Chaves de acesso salvas apenas com consentimento explícito, só neste dispositivo. */
export const savedKeys = {
  list: () => readStore(CREDS_KEY, []),
  save: (secret_key) => {
    const current = readStore(CREDS_KEY, []).filter((p) => p.secret_key !== secret_key);
    writeStore(CREDS_KEY, [{ secret_key, saved_at: Date.now() }, ...current]);
  },
  remove: (secret_key) => writeStore(CREDS_KEY, readStore(CREDS_KEY, []).filter((p) => p.secret_key !== secret_key)),
};

/** Exibe a chave em blocos de 4 caracteres (o valor copiado continua sem espaços). */
export const formatKeyBlocks = (key) => (key || "").replace(/(.{4})/g, "$1 ").trim();

let statusCache = { at: 0, data: null };

/** Sinal de novidade a partir das chaves guardadas neste dispositivo (sem expor texto). */
export const anonymousStatus = async ({ force = false } = {}) => {
  const stored = readStore(CREDS_KEY, []).slice(0, 3);
  if (stored.length === 0) return null;
  if (!force && Date.now() - statusCache.at < 60000) return statusCache.data;
  let unread = 0;
  let last = null;
  for (const item of stored) {
    try {
      const { data } = await publicApi.post("/anonymous-questions/status", { secret_key: item.secret_key });
      unread += data.unread_count || 0;
      last = data;
    } catch {
      /* chave inválida ou sem conexão: sem aviso */
    }
  }
  statusCache = { at: Date.now(), data: last ? { ...last, unread_count: unread } : null };
  return statusCache.data;
};

export const trackEvent = (event_type, payload = {}) => {
  publicApi.post("/events", { event_type, ...payload }).catch(() => {});
};

export const TOPIC_LABELS = {
  tratamentos: "Tratamentos",
  depressao_burnout: "Depressão e burnout",
  dependencia_alcool_substancias: "Dependência de álcool ou substâncias",
  dependencia_medicamentos: "Dependência de medicamentos",
  jogos_apostas: "Jogos e apostas",
  avaliacao_admissao: "Avaliação e admissão",
  custos_atendimento: "Custos e formas de atendimento",
  ajudar_outra_pessoa: "Como ajudar outra pessoa",
  outros: "Outros assuntos",
};

export const THREAD_STATUS_LABELS = {
  received: "Recebida",
  in_analysis: "Em análise",
  answered: "Respondida",
  waiting_for_user: "Aguardando você",
  closed: "Encerrada",
  blocked_as_abuse: "Bloqueada",
};

export const CONTENT_TYPE_LABELS = {
  article: "Artigo",
  guide: "Guia curto",
  faq: "Pergunta frequente",
  treatment: "Tratamento",
  institutional_page: "Página institucional",
  policy: "Política",
  banner: "Banner",
  notice: "Aviso",
  app_setting: "Configuração pública",
};

export const CONTENT_STATUS_LABELS = {
  draft: "Rascunho",
  in_review: "Em revisão",
  approved: "Aprovado",
  scheduled: "Agendado",
  published: "Publicado",
  archived: "Arquivado",
};
